import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon, SearchIcon, XIcon } from 'lucide-react';
import { articles } from '../data/articles';
import { usePageMeta } from '../hooks/usePageMeta';
const ALL = 'All';

/**
 * Index of every guide article, with search and category filtering.
 *
 * Search matches the title, standfirst, category, and target keyword, so a
 * reader typing "nursing" or "accreditation" finds the article whether or not
 * the word is in its headline.
 */
export function ArticlesPage() {
  usePageMeta('Online Degree Guides & Articles | Online College Guide', 'Browse in-depth guides on accredited online degree programs, admissions, financial aid, and careers. Get matched by career goals, schedule, and budget.');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ALL);
  const categories = useMemo(() => {
    const unique = Array.from(new Set(articles.map(a => a.category)));
    return [ALL, ...unique.sort()];
  }, []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter(article => {
      if (category !== ALL && article.category !== category) return false;
      if (!q) return true;
      return [article.title, article.intro, article.category, article.targetKeyword, article.eyebrow].join(' ').toLowerCase().includes(q);
    });
  }, [query, category]);
  const isFiltered = query.trim() !== '' || category !== ALL;
  const clearFilters = () => {
    setQuery('');
    setCategory(ALL);
  };
  return <main className="bg-white" data-id="element-934" data-mp="2ioel4kebpf8p">
      {/* Hero */}
      <header className="hero-bg" data-id="element-935" data-mp="2in3m0xvtovnx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-936" data-mp="2im0s0h37ed3s">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-937" data-mp="jd3q810vpibt">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-938" data-mp="o9kgb0rn26rj">
              <li data-id="element-939" data-mp="1rkrhknihyfsz">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-940" data-mp="1rmazsjnpr0xe">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-941" data-mp="1mo9j25drfdp3">/</li>
              <li className="text-white/90" data-id="element-942" data-mp="20mhbq13rjsug">Articles</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-943" data-mp="1vn4qfksjittz">
            Guides to Online Degree Programs
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-944" data-mp="2ai15ohyrohgk">
            In-depth, plain-language guides on accreditation, admissions,
            financial aid, and where each degree actually leads. No sign-up, no
            obligation.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-945" data-mp="2fcyk64etfj0u">
        {/* Search + filters */}
        <div className="mb-8" data-id="element-946" data-mp="2t8adc7dbp1fn">
          <label htmlFor="article-search" className="sr-only" data-id="element-947" data-mp="2ocbko7kyox9f">
            Search articles
          </label>
          <div className="relative max-w-xl" data-id="element-948" data-mp="37ydgdkrz5tv2">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-949" data-mp="37v97ia3ke9y2" />
            <input id="article-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search guides — try “nursing” or “accreditation”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-950" data-mp="2xywbv6oglm0o" />
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4" role="group" aria-label="Filter by category" data-id="element-951" data-mp="2k7823a1on9mv">
            {categories.map(name => {
            const isActive = category === name;
            return <button key={name} type="button" onClick={() => setCategory(name)} aria-pressed={isActive} className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-200 ${isActive ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy hover:text-navy'}`} data-id="element-952" data-mp="236dltmhmf5s4">
                  {name}
                </button>;
          })}
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-953" data-mp="1ah6cdsgmovs3">
            <span aria-live="polite" data-id="element-954" data-mp="15imqtt5y5m09">
              {results.length} {results.length === 1 ? 'guide' : 'guides'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-955" data-mp="34v7cshrttqua">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-956" data-mp="38dejfk77ivcm" />
                Clear filters
              </button>}
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-957" data-mp="3h8b3j7sgtsf9">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-958" data-mp="3m75syskxuf8c">
              No guides match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-959" data-mp="2tge30cugumtg">
              Try a broader term, or get matched with accredited programs
              directly — it takes about three minutes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-960" data-mp="33f39l9gwwkue">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-961" data-mp="dp8a408cwct4">
                Show all guides
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-962" data-mp="3k6qmdup7zoej">
                Get Matched Now
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-963" data-mp="3u2tqh63mtx44" />
              </Link>
            </div>
          </div> : <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-964" data-mp="31gs6isf6kr0c">
            {results.map(article => <li key={article.slug} className="flex" data-id="element-965" data-mp="3g5okf63x4y8v">
                <Link to={`/article/${article.slug}`} className="group flex flex-col w-full border border-gray-200 rounded-xl p-6 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-966" data-mp="3begckei8lkxt">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-crimson mb-3" data-id="element-967" data-mp="y771wy6qbge5">
                    {article.category}
                  </p>
                  <h2 className="text-lg font-bold text-navy leading-snug mb-3 group-hover:underline" data-id="element-968" data-mp="o9ar67hpengk">
                    {article.title}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6 line-clamp-3" data-id="element-969" data-mp="t8x044qi1hjk">
                    {article.intro}
                  </p>
                  <div className="mt-auto pt-4 border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500" data-id="element-970" data-mp="1lwsw1788v5fa">
                    <span className="inline-flex items-center gap-1.5" data-id="element-971" data-mp="17wi7wbybk4xt">
                      <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-972" data-mp="1by3q8ctmhbfe" />
                      {article.updated}
                    </span>
                    <span className="inline-flex items-center gap-1.5" data-id="element-973" data-mp="khprvibbnek">
                      <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-974" data-mp="s7sw1iwo0azz" />
                      {article.readMinutes} min read
                    </span>
                  </div>
                </Link>
              </li>)}
          </ul>}
      </div>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-975" data-mp="e81ljs5cxcqq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-976" data-mp="e8tf6xfe90ed">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-977" data-mp="1awpodh8i4tfk">
            Skip the research — get matched instead
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-978" data-mp="16ya61qnd898h">
            Answer a few questions about your goals, schedule, and budget, and
            we'll connect you with accredited programs that fit. No cost, no
            obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-979" data-mp="1umteq7abuo13">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-980" data-mp="2259e8qokvh3s" />
          </Link>
        </div>
      </section>
    </main>;
}